export function getTodayAtLocalMidnight(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export function dateToISOStartOfDay(date: Date): string {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${y}-${pad(m + 1)}-${pad(d)}T00:00:00.000Z`;
}

export function dateToISOEndOfDay(date: Date): string {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${y}-${pad(m + 1)}-${pad(d)}T23:59:59.999Z`;
}

/** Convierte valor a Date o null (solo para visualización, no para enviar a API). */
export function toDateOrNull(value: Date | string | null | undefined): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Formato visual de fecha: "Hoy, dd/mm/yyyy", "Mañana, dd/mm/yyyy", "Ayer, dd/mm/yyyy" o "dd/mm/yyyy".
 * Solo para mostrar al usuario; no modifica el valor que se envía a la API.
 * @param date Fecha a formatear
 * @param translate Función de traducción (ej. key => translateService.translate(key))
 * @param dateKeys Objeto con keys de i18n: today, tomorrow, yesterday (default: invitation.date.*)
 */
export function formatDateForDisplay(
  date: Date | null,
  translate: (key: string) => string,
  dateKeys: { today: string; tomorrow: string; yesterday: string } = {
    today: 'invitation.date.today',
    tomorrow: 'invitation.date.tomorrow',
    yesterday: 'invitation.date.yesterday'
  }
): string {
  if (!date || isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const ddMmYyyy = `${day}/${month}/${year}`;

  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const today = getTodayAtLocalMidnight();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (d.getTime() === today.getTime()) {
    return `${translate(dateKeys.today)}, ${ddMmYyyy}`;
  }
  if (d.getTime() === tomorrow.getTime()) {
    return `${translate(dateKeys.tomorrow)}, ${ddMmYyyy}`;
  }
  if (d.getTime() === yesterday.getTime()) {
    return `${translate(dateKeys.yesterday)}, ${ddMmYyyy}`;
  }
  return ddMmYyyy;
}

/** Opciones por defecto para las keys de i18n en formatDateTimeForDisplay */
const DEFAULT_DATE_TIME_KEYS = {
  today: 'invitation.date.today',
  tomorrow: 'invitation.date.tomorrow',
  yesterday: 'invitation.date.yesterday'
} as const;

/**
 * Formato visual de fecha y hora: parte fecha como formatDateForDisplay (Hoy/Mañana/Ayer, dd/mm/yyyy)
 * y hora en 12h con AM/PM usando Intl (estándar del navegador).
 * @param date Fecha a formatear
 * @param translate Función de traducción para Hoy/Mañana/Ayer
 * @param locale Locale para la hora (ej. 'es', 'en'). Por defecto 'es'.
 * @param dateKeys Keys de i18n para today, tomorrow, yesterday
 * @returns String con fecha y hora separadas por \n (ej. "Hoy, 05/02/2025\n2:30 PM")
 */
export function formatDateTimeForDisplay(
  date: Date | null,
  translate: (key: string) => string,
  locale: string = 'es',
  dateKeys: { today: string; tomorrow: string; yesterday: string } = DEFAULT_DATE_TIME_KEYS
): string {
  if (!date || isNaN(date.getTime())) return '';

  const datePart = formatDateForDisplay(date, translate, dateKeys);
  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  const timePart = timeFormatter.format(date);
  return `${datePart}\n${timePart}`;
}
